import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const fmtDateTime = (x) => (x ? new Date(x).toLocaleString() : "-");
const fmtTime = (x) => new Date(x).toLocaleTimeString();

const HEAD = { fillColor: [10, 10, 10], textColor: [255, 184, 0], fontStyle: "bold" };
const BASE = { styles: { fontSize: 9, cellPadding: 2 }, headStyles: HEAD, theme: "grid" };

function timesByDumperMap(report) {
  const times = {};
  (report.trip_log || []).forEach((t) => {
    (times[t.dumper_number] = times[t.dumper_number] || []).push(t.timestamp);
  });
  Object.values(times).forEach((a) => a.reverse());
  return times;
}

function shovelTimeTables(report) {
  const timesMap = timesByDumperMap(report);
  return (report.per_shovel || []).map((s) => {
    const drows = (report.per_dumper || []).filter((d) => d.shovel_number === s.shovel_number);
    const maxT = Math.max(0, ...drows.map((d) => (timesMap[d.dumper_number] || []).length));
    const head = ["Dumper", "Operator", ...Array.from({ length: maxT }, (_, i) => `Trip ${i + 1}`)];
    const body = drows.map((d) => [
      d.dumper_number,
      d.dumper_operator || "-",
      ...Array.from({ length: maxT }, (_, i) => (timesMap[d.dumper_number]?.[i] ? fmtTime(timesMap[d.dumper_number][i]) : "-")),
    ]);
    return { shovel: s, maxT, head, body };
  });
}

export function exportReportPDF(report, mine) {
  const doc = new jsPDF();
  const s = report.shift;
  doc.setFontSize(16);
  doc.text("DumpOps Hub - Shift Trip Report", 14, 15);
  doc.setFontSize(10);
  doc.text(`Area: ${mine?.area_name || "-"}    Mine: ${mine?.mine_name || "-"}`, 14, 23);
  doc.text(`Shift: ${s.name} (${s.status})`, 14, 29);
  doc.text(`Started: ${fmtDateTime(s.started_at)}    Ended: ${fmtDateTime(s.ended_at)}`, 14, 35);
  doc.setFontSize(12);
  doc.text(`Total Trips: ${report.total_trips}    Coal: ${report.coal_trips}    OB: ${report.ob_trips}`, 14, 43);

  doc.setFontSize(12);
  doc.text("Trips per Shovel", 14, 46);
  autoTable(doc, {
    ...BASE,
    startY: 48,
    head: [["Shovel", "Operator", "Material", "Dumpers", "Trips"]],
    body: report.per_shovel.map((x) => [x.shovel_number, x.shovel_operator || "-", x.material, x.dumpers.join(", "), String(x.trips)]),
  });

  let y = doc.lastAutoTable.finalY + 10;
  doc.text("Trips per Dumper", 14, y - 2);
  autoTable(doc, {
    ...BASE,
    startY: y,
    head: [["Dumper", "Operator", "Shovel", "Material", "Trips"]],
    body: report.per_dumper.map((x) => [x.dumper_number, x.dumper_operator || "-", x.shovel_number, x.material, String(x.trips)]),
  });

  shovelTimeTables(report).forEach(({ shovel, maxT, head, body }) => {
    if (maxT === 0) return;
    y = doc.lastAutoTable.finalY + 10;
    doc.text(
      `Shovel ${shovel.shovel_number} (${shovel.material})${shovel.shovel_operator ? " - " + shovel.shovel_operator : ""}`,
      14,
      y - 2
    );
    autoTable(doc, { ...BASE, startY: y, head: [head], body });
  });

  doc.save(`trip-report-${s.name.replace(/\s+/g, "-")}.pdf`);
}

export function exportReportWord(report, mine) {
  const s = report.shift;
  const table = (head, body) =>
    `<table border="1" cellspacing="0" cellpadding="6" style="border-collapse:collapse;width:100%;margin-bottom:16px;">` +
    `<tr style="background:#0A0A0A;color:#FFB800;">${head.map((h) => `<th style="text-align:left;">${h}</th>`).join("")}</tr>` +
    body.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("") +
    `</table>`;

  const shovelSections = shovelTimeTables(report)
    .filter((x) => x.maxT > 0)
    .map(
      ({ shovel, head, body }) =>
        `<h3>Shovel ${shovel.shovel_number} (${shovel.material})${shovel.shovel_operator ? " - " + shovel.shovel_operator : ""}</h3>` +
        table(head, body)
    )
    .join("");

  const html = `
    <h1>DumpOps Hub - Shift Trip Report</h1>
    <p><b>Area:</b> ${mine?.area_name || "-"} &nbsp; <b>Mine:</b> ${mine?.mine_name || "-"}<br/>
    <b>Shift:</b> ${s.name} (${s.status})<br/>
    <b>Started:</b> ${fmtDateTime(s.started_at)} &nbsp; <b>Ended:</b> ${fmtDateTime(s.ended_at)}</p>
    <h2>Total Trips: ${report.total_trips} &nbsp; Coal: ${report.coal_trips} &nbsp; OB: ${report.ob_trips}</h2>
    <h3>Trips per Shovel</h3>
    ${table(["Shovel", "Operator", "Material", "Dumpers", "Trips"], report.per_shovel.map((x) => [x.shovel_number, x.shovel_operator || "-", x.material, x.dumpers.join(", "), x.trips]))}
    <h3>Trips per Dumper</h3>
    ${table(["Dumper", "Operator", "Shovel", "Material", "Trips"], report.per_dumper.map((x) => [x.dumper_number, x.dumper_operator || "-", x.shovel_number, x.material, x.trips]))}
    ${shovelSections ? `<h3>Shovel-wise Dumper Trip Times</h3>${shovelSections}` : ""}
  `;

  const blob = new Blob(
    [`<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"/></head><body>${html}</body></html>`],
    { type: "application/msword" }
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `trip-report-${s.name.replace(/\s+/g, "-")}.doc`;
  a.click();
  URL.revokeObjectURL(url);
}
